import type { AxiosInstance, AxiosResponse } from "axios";
import { useClient } from "../state/client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/solid-query";
import { initialized } from "../utils/initializedUtil";
import type { MoreAppError } from "../MoreAppError";
import type { User } from "../state/user";
import { useUserState } from "../state/user";
import { toUser } from "../utils/mappers/user";
import { minutes, seconds } from "../utils/timeUtil";
import type { LanguageCode } from "../state/i18n";
import type CustomerId from "../state/CustomerId";
import { toGroup, toGroupWithMemberCount } from "../utils/mappers/group";
import { toRole } from "../utils/mappers/role";
import type { JsonPatch } from "../types/JsonPatch";
import type { Page } from "../types/Page";
import type { Pagination } from "./ApiService";
import { doNotRetryOnUnrecoverableError } from "../utils/apiUtil";
import type { Accessor } from "solid-js";
import type UserId from "../state/UserId";
import type GroupId from "../state/GroupId";
import { toGroupUser } from "../utils/mappers/groupUser";
import { isEqual, isNil } from "lodash-es";
import type RoleId from "../state/RoleId";
import { toAccountOwner } from "../utils/mappers/accountOwner";
import type { AccountOwner } from "../types/AccountOwner";
import type { Role } from "../state/role";
import type { PermissionType } from "../state/Permission";
import { compareByLocale } from "../utils/stringUtil";
import type { Customer } from "../state/customer";
import type FolderId from "../state/FolderId";
import type CustomerUid from "../state/CustomerUid";
import { resolveGrantOperation } from "../utils/roleUtil";
import type FormId from "../state/FormId";
import { toInvite } from "../utils/mappers/invite.ts";
import InviteId from "../state/InviteId.ts";

export type AuthenticationResponse = {
  roles: Record<string, string[]>;
};

export type UserDto = {
  id: string;
  username: string;
  emailAddress: string;
  settings: UserSettingsDto;
  emailValidated?: boolean;
  grants: GrantDto[];
  groups: string[];
  externallyManaged: boolean;
  disabled: boolean;
};

export type UserSettingsDto = {
  firstName?: string | null;
  lastName?: string | null;
  language?: LanguageCode;
  phoneNumber?: string | null;
  receiveNewsLetter: boolean;
  finishedTour: boolean;
  timeZone?: string;
};

export type GroupDto = {
  id: string;
  name: string;
  grants: GrantDto[];
  externallyManaged: boolean;
};

export type GroupWithMemberCountDto = GroupDto & { memberCount: number };

export type InviteDto = {
  id: string;
  emailAddress: string;
  groups: string[];
  grants: GrantDto[];
};

export type GrantDto = {
  customerId: number;
  resourceType: ResourceTypeDto;
  roleId: string;
  resourceId: string;
};

export type RoleDto = {
  id: string;
  name: string;
  description?: string;
  permissions: string[];
  readOnly: boolean;
  translatableKey?: string;
};

export type GroupUserDto = {
  id: string;
  username: string;
  firstName: string;
  lastName: string;
  grants: GrantDto[];
  externallyManaged: boolean;
  disabled: boolean;
};

export type ResourceTypeDto = "CUSTOMER" | "FORM" | "FOLDER" | "SYSTEM" | "NONE";

export type AccountOwnerDto = {
  id: string;
  firstName: string;
  lastName: string;
  emailAddress: string;
};

export type ChangePasswordRequest = {
  currentPassword: string;
  newPassword: string;
};

export type InviteUserRequest = {
  emailAddress: string;
  language: LanguageCode;
  groups: GroupId[];
  grants: GrantDto[];
};

export type TransferOwnershipRequest = { user: UserId };

export type UpsertRoleRequest = {
  name: string;
  description?: string;
  permissions: PermissionType[];
};

export type UpdateGrantVariables = {
  userOrInviteId: UserId | InviteId;
  isInvited: boolean;
  roleId: RoleId | null;
  currentRoleId: RoleId | null;
} & (
  | { resourceType: "CUSTOMER"; resourceId: CustomerUid }
  | { resourceType: "FOLDER"; resourceId: FolderId }
  | { resourceType: "FORM"; resourceId: FormId }
);

export type UpdateGroupGrantVariables = {
  groupId: GroupId;
  roleId: RoleId | null;
  currentRoleId: RoleId | null;
} & (
  | { resourceType: "CUSTOMER"; resourceId: CustomerUid }
  | { resourceType: "FOLDER"; resourceId: FolderId }
  | { resourceType: "FORM"; resourceId: FormId }
);

export type UserStatusUpdate = {
  finishedTour: boolean;
  customerId: CustomerId;
  referralSource?: string;
  referralSourceOther?: string;
  goals?: string[];
  goalsOther?: string;
  size: string;
};

let client: AxiosInstance | null = null;

const init = (): void => {
  client = useClient();
};

const getClient = () => initialized(client, "client");

const UserService = {
  getUser: () =>
    useQuery<User, MoreAppError>(() => ({
      queryKey: ["currentUser"],
      queryFn: async () => {
        const response = await getClient().get<UserDto>(`api/v1.0/users/current`);
        return toUser(response.data);
      },
      staleTime: Infinity,
    })),
  getUserById: (customerId: CustomerId, userId: UserId, retry = true) =>
    useQuery<User, MoreAppError>(() => ({
      queryKey: ["user", userId.toString()],
      queryFn: async () => {
        const response = await getClient().get<UserDto>(`api/v1.0/customers/${customerId}/users/${userId}`);
        return toUser(response.data);
      },
      staleTime: minutes(10),
      retry: retry ? doNotRetryOnUnrecoverableError : undefined,
    })),
  getPermissions: () =>
    useQuery(() => ({
      queryKey: ["user", "permissions"],
      queryFn: async () => {
        const { data } = await getClient().get<AuthenticationResponse>(`api/v1.0/authentication`);
        return data.roles;
      },
      staleTime: seconds(60),
    })),
  getVisitorToken: async (): Promise<{ token: string }> => {
    const { data } = await getClient().get<{ token: string }>(`api/v1.0/client/users/visitors/token`);
    return data;
  },
  getUsers: (customerId: CustomerId) =>
    useQuery(() => ({
      queryKey: ["users"],
      queryFn: async () =>
        getClient()
          .get<UserDto[]>(`api/v1.0/customers/${customerId}/users`)
          .then((response) => response.data.map(toUser)),
    })),
  searchMembers: (
    customerId: CustomerId,
    query: { id?: string; firstName?: string; lastName?: string; username?: string },
    size = 10,
  ) =>
    getClient()
      .get<Page<UserDto>>(`api/v1.0/customers/${customerId}/users/page`, {
        params: { page: 0, size, filterOperator: "OR", ...query },
      })
      .then((response) =>
        response.data.content.map((dto) => {
          if (isNil(dto.username)) {
            return toInvite(dto);
          } else {
            return toUser(dto);
          }
        }),
      ),
  getPaginatedUsers: (customerId: CustomerId, pagination: Accessor<Pagination>, groupId?: GroupId) =>
    useQuery(() => ({
      queryKey: ["users", groupId?.toString(), pagination()],
      queryFn: async () =>
        getClient()
          .get<Page<UserDto>>(`api/v1.0/customers/${customerId}/users/page`, {
            params: { page: pagination().page, size: pagination().size, groupId },
          })
          .then((response) => ({
            content: response.data.content.map((dto) => {
              if (isNil(dto.username)) {
                return toInvite(dto);
              } else {
                return toUser(dto);
              }
            }),
            first: response.data.first,
            last: response.data.last,
            totalPages: response.data.totalPages,
            totalElements: response.data.totalElements,
          })),
    })),
  getGroup: (customerId: CustomerId, groupId: GroupId) =>
    useQuery(() => ({
      queryKey: ["groups", groupId.toString()],
      queryFn: async () =>
        getClient()
          .get<GroupDto>(`api/v2/customers/${customerId}/groups/${groupId}`)
          .then((response) => toGroup(response.data)),
    })),
  getGroups: (customerId: CustomerId) =>
    useQuery(() => ({
      queryKey: ["customers", customerId, "groups"],
      queryFn: async () =>
        getClient()
          .get<GroupWithMemberCountDto[]>(`api/v2/customers/${customerId}/groups`)
          .then((response) =>
            response.data.map(toGroupWithMemberCount).sort((a, b) => compareByLocale(a.name, b.name)),
          ),
    })),
  getRoles: (customerId: CustomerId) =>
    useQuery(() => ({
      queryKey: ["customers", customerId, "roles"],
      queryFn: async () =>
        getClient()
          .get<RoleDto[]>(`api/v2/customers/${customerId}/roles`)
          .then((response) => response.data.map(toRole)),
    })),
  getGroupUsers: (customerId: CustomerId, groupId: GroupId) =>
    useQuery(() => ({
      queryKey: ["groups", groupId.toString(), "users"],
      queryFn: async () =>
        getClient()
          .get<GroupUserDto[]>(`api/v2/customers/${customerId}/groups/${groupId}/users`)
          .then((response) => response.data.map(toGroupUser)),
    })),
  patchUser: () => {
    const queryClient = useQueryClient();
    return useMutation<void, MoreAppError, JsonPatch[]>(() => ({
      mutationFn: async (value) => {
        await getClient().patch(`api/v1.0/users`, value, {
          headers: { "Content-Type": "application/json-patch+json" },
        });
      },
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ["currentUser"] });
      },
    }));
  },
  removeUser: (customerId: CustomerId) => {
    const queryClient = useQueryClient();
    return useMutation<void, MoreAppError, UserId>(() => ({
      mutationFn: async (value) => {
        await getClient().delete(`/api/v1.0/customers/${customerId}/users/${value}`);
      },
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ["users"] });
        void queryClient.invalidateQueries({ queryKey: ["customers", customerId, "groups"] });
      },
    }));
  },
  changePassword: () =>
    useMutation<void, MoreAppError, ChangePasswordRequest>(() => ({
      mutationFn: async (value) => {
        await getClient().post(`api/v1.0/users/change-password`, value);
      },
    })),
  updateGrant: (customer: Customer) => {
    const queryClient = useQueryClient();
    return useMutation<void, MoreAppError, UpdateGrantVariables>(() => ({
      mutationFn: async (variables) => {
        const operation = resolveGrantOperation(variables.roleId, variables.currentRoleId);
        if (isNil(operation)) {
          return; // no-op: "no access" -> "no access"
        }
        const segment = variables.isInvited ? "invites" : "users";
        await getClient().patch(
          `api/v2/customers/${customer.customerId}/${segment}/${variables.userOrInviteId}/grants`,
          {
            operation,
            resourceId: variables.resourceId,
            resourceType: variables.resourceType,
            ...(variables.roleId !== null ? { roleId: variables.roleId } : {}),
          },
        );
      },
      onSuccess: async (_, { isInvited, userOrInviteId }) => {
        if (isInvited) {
          await Promise.all([
            queryClient.invalidateQueries({ queryKey: ["invites", userOrInviteId.toString()] }),
            queryClient.invalidateQueries({ queryKey: ["customers", customer.customerId, "invites"] }),
          ]);
        } else {
          await Promise.all([
            queryClient.invalidateQueries({ queryKey: ["user", userOrInviteId.toString()] }),
            queryClient.invalidateQueries({ queryKey: ["users"] }),
          ]);
        }
      },
    }));
  },

  updateGroupGrant: (customer: Customer) => {
    const queryClient = useQueryClient();
    return useMutation<void, MoreAppError, UpdateGroupGrantVariables>(() => ({
      mutationFn: async (variables) => {
        const operation = resolveGrantOperation(variables.roleId, variables.currentRoleId);
        if (isNil(operation)) {
          return; // no-op: "no access" -> "no access"
        }
        await getClient().patch(`api/v2/customers/${customer.customerId}/groups/${variables.groupId}/grants`, {
          operation,
          resourceId: variables.resourceId,
          resourceType: variables.resourceType,
          ...(variables.roleId !== null ? { roleId: variables.roleId } : {}),
        });
      },
      onSuccess: async (_, { groupId }) => {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ["groups", groupId.toString()] }),
          queryClient.invalidateQueries({ queryKey: ["customers", customer.customerId, "groups"] }),
        ]);
      },
    }));
  },

  inviteUser: (customerId: CustomerId) => {
    const queryClient = useQueryClient();
    return useMutation<void, MoreAppError, InviteUserRequest>(() => ({
      mutationFn: async (value) => {
        await getClient().post(`api/v2/customers/${customerId}/invites`, value);
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["users"] });
        queryClient.invalidateQueries({ queryKey: ["customers", customerId, "groups"] });
      },
    }));
  },
  inviteUsers: (customerId: CustomerId) => {
    const queryClient = useQueryClient();

    return useMutation<void, MoreAppError, FormData>(() => ({
      mutationFn: async (value) => {
        await getClient().post(`api/v1.0/customers/${customerId}/users/import`, value);
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: ["users"] });
        await queryClient.invalidateQueries({ queryKey: ["customers", customerId, "groups"] });
      },
    }));
  },
  resendInvitation: (customerId: CustomerId) =>
    useMutation<void, MoreAppError, InviteId>(() => ({
      mutationFn: async (value) => {
        await getClient().get(`api/v2/customers/${customerId}/invites/${value}/resend`);
      },
    })),
  revokeInvitation: (customerId: CustomerId) => {
    const queryClient = useQueryClient();

    return useMutation<void, MoreAppError, InviteId>(() => ({
      mutationFn: async (value) => {
        await getClient().delete(`/api/v2/customers/${customerId}/invites/${value}`);
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["users"] });
        queryClient.invalidateQueries({ queryKey: ["customers", customerId, "groups"] });
      },
    }));
  },
  getInvites: (customerId: CustomerId) =>
    useQuery(() => ({
      queryKey: ["customers", customerId, "invites"],
      queryFn: async () =>
        getClient()
          .get<InviteDto[]>(`api/v2/customers/${customerId}/invites`)
          .then((response) => response.data.map(toInvite)),
    })),
  getInvite: (customerId: CustomerId, inviteId: InviteId, retry = false) =>
    useQuery(() => ({
      queryKey: ["invites", inviteId.toString()],
      queryFn: async () =>
        getClient()
          .get<InviteDto>(`/api/v2/customers/${customerId}/invites/${inviteId}`)
          .then((response) => toInvite(response.data)),
      staleTime: minutes(10),
      retry: retry ? doNotRetryOnUnrecoverableError : undefined,
    })),
  updateCustomerUserSettings: (customerId: CustomerId, userId: UserId) => {
    const queryClient = useQueryClient();
    const currentUser = useUserState();

    return useMutation<void, MoreAppError, UserSettingsDto>(() => ({
      mutationFn: async (value) =>
        await getClient().put(`/api/v1.0/customers/${customerId}/users/${userId}/settings`, value),
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: ["users"] });
        await queryClient.invalidateQueries({ queryKey: ["user", userId.toString()] });
        if (isEqual(currentUser().id, userId)) {
          await queryClient.invalidateQueries({ queryKey: ["currentUser"] });
        }
      },
    }));
  },
  createGroup: (customerId: CustomerId) => {
    const queryClient = useQueryClient();

    return useMutation<void, MoreAppError, { name: string }>(() => ({
      mutationFn: async (value) => await getClient().post(`/api/v2/customers/${customerId}/groups`, value),
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: ["customers", customerId, "groups"] });
      },
    }));
  },
  patchGroup: (customerId: CustomerId, groupId: GroupId) => {
    const queryClient = useQueryClient();

    return useMutation<void, MoreAppError, GroupDto>(() => ({
      mutationFn: async (value) => await getClient().patch(`/api/v2/customers/${customerId}/groups/${groupId}`, value),
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: ["customers", customerId, "groups"] });
        await queryClient.invalidateQueries({ queryKey: ["groups", groupId.toString()] });
      },
    }));
  },
  addUsersToGroup: (customerId: CustomerId, groupId: GroupId) => {
    const queryClient = useQueryClient();

    return useMutation<void, MoreAppError, { users: (UserId | InviteId)[] }>(() => ({
      mutationFn: async ({ users }) => {
        for (const userId of users) {
          const path =
            userId instanceof InviteId
              ? `/api/v2/customers/${customerId}/invites/${userId}/groups/${groupId}`
              : `/api/v2/customers/${customerId}/groups/${groupId}/users/${userId}`;
          await getClient().post(path);
        }
      },
      onSuccess: async (_, { users }) => {
        await queryClient.invalidateQueries({ queryKey: ["groups", groupId.toString(), "users"] });
        await queryClient.invalidateQueries({ queryKey: ["users", groupId.toString()] });
        await queryClient.invalidateQueries({ queryKey: ["customers", customerId, "groups"] });
        await queryClient.invalidateQueries({ queryKey: ["customers", customerId, "invites"] });

        for (const id of users) {
          await queryClient.invalidateQueries({
            queryKey: id instanceof InviteId ? ["invites", id.toString()] : ["user", id.toString()],
          });
        }
      },
    }));
  },
  addUserToGroups: (customerId: CustomerId) => {
    const queryClient = useQueryClient();

    return useMutation<void, MoreAppError, { id: UserId | InviteId; groups: GroupId[] }>(() => ({
      mutationFn: async ({ groups, id: userId }) => {
        for (const group of groups) {
          const path =
            userId instanceof InviteId
              ? `/api/v2/customers/${customerId}/invites/${userId}/groups/${group}`
              : `/api/v2/customers/${customerId}/groups/${group}/users/${userId}`;
          await getClient().post(path);
        }
      },
      onSuccess: async (_, { id, groups }) => {
        for (const groupId of groups) {
          await queryClient.invalidateQueries({ queryKey: ["groups", groupId.toString(), "users"] });
          await queryClient.invalidateQueries({ queryKey: ["users", groupId.toString()] });
        }

        await queryClient.invalidateQueries({ queryKey: ["customers", customerId, "groups"] });
        await queryClient.invalidateQueries({
          queryKey: id instanceof InviteId ? ["invites", id.toString()] : ["user", id.toString()],
        });
      },
    }));
  },
  removeUserFromGroup: (customerId: CustomerId) => {
    const queryClient = useQueryClient();

    return useMutation<void, MoreAppError, { id: UserId | InviteId; groupId: GroupId }>(() => ({
      mutationFn: async ({ id, groupId }) => {
        const path =
          id instanceof InviteId
            ? `/api/v2/customers/${customerId}/invites/${id}/groups/${groupId}`
            : `/api/v2/customers/${customerId}/groups/${groupId}/users/${id}`;
        await getClient().delete(path);
      },
      onSuccess: async (_, { id, groupId }) => {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ["users", groupId.toString()] }),
          queryClient.invalidateQueries({ queryKey: ["groups", groupId.toString(), "users"] }),
          queryClient.invalidateQueries({ queryKey: ["customers", customerId, "groups"] }),
          queryClient.invalidateQueries({
            queryKey: id instanceof InviteId ? ["invites", id.toString()] : ["user", id.toString()],
          }),
        ]);
      },
    }));
  },
  deleteGroup: (customerId: CustomerId, groupId: GroupId) => {
    const queryClient = useQueryClient();

    return useMutation<void, MoreAppError, void>(() => ({
      mutationFn: async () => await getClient().delete(`/api/v2/customers/${customerId}/groups/${groupId}`),
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: ["customers", customerId, "groups"] });
        void queryClient.invalidateQueries({ queryKey: ["groups", groupId.toString()], refetchType: "none" });
      },
    }));
  },
  createRole: (customerId: CustomerId) => {
    const queryClient = useQueryClient();

    return useMutation<Role, MoreAppError, UpsertRoleRequest>(() => ({
      mutationFn: async ({ name, description, permissions }) => {
        const { data } = await getClient().post<RoleDto, AxiosResponse<RoleDto>, UpsertRoleRequest>(
          `/api/v2/customers/${customerId}/roles`,
          {
            name,
            description,
            permissions,
          },
        );
        return toRole(data);
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: ["customers", customerId, "roles"] });
      },
    }));
  },
  updateRole: (customerId: CustomerId, roleId: RoleId) => {
    const queryClient = useQueryClient();
    return useMutation<Role, MoreAppError, UpsertRoleRequest>(() => ({
      mutationFn: async ({ name, description, permissions }) => {
        const { data } = await getClient().patch<RoleDto, AxiosResponse<RoleDto>, UpsertRoleRequest>(
          `/api/v2/customers/${customerId}/roles/${roleId}`,
          {
            name,
            description,
            permissions,
          },
        );
        return toRole(data);
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: ["customers", customerId, "roles"] });
      },
    }));
  },
  deleteRole: (customerId: CustomerId, roleId: RoleId) => {
    const queryClient = useQueryClient();

    return useMutation<void, MoreAppError, void>(() => ({
      mutationFn: async () => await getClient().delete(`/api/v2/customers/${customerId}/roles/${roleId}`),
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: ["customers", customerId, "roles"] });
        await queryClient.invalidateQueries({ queryKey: ["roles", roleId.toString()] });
      },
    }));
  },
  getUsersByRole: (customerId: CustomerId, roleId: RoleId) => {
    const queryClient = useQueryClient();

    return queryClient.fetchQuery({
      queryKey: ["role", roleId.toString(), "users"],
      queryFn: async () => {
        const { data } = await getClient().get<UserDto[]>(`/api/v2/customers/${customerId}/roles/${roleId}/users`);

        return data.map(toUser);
      },
    });
  },
  getGroupsByRole: (customerId: CustomerId, roleId: RoleId) => {
    const queryClient = useQueryClient();

    return queryClient.fetchQuery({
      queryKey: ["role", roleId.toString(), "groups"],
      queryFn: async () => {
        const { data } = await getClient().get<GroupDto[]>(`/api/v2/customers/${customerId}/roles/${roleId}/groups`);

        return data.map(toGroup).sort((a, b) => compareByLocale(a.name, b.name));
      },
    });
  },
  getInvitesByRole: (customerId: CustomerId, roleId: RoleId) => {
    const queryClient = useQueryClient();

    return queryClient.fetchQuery({
      queryKey: ["role", roleId.toString(), "invites"],
      queryFn: async () => {
        const { data } = await getClient().get<InviteDto[]>(`/api/v2/customers/${customerId}/roles/${roleId}/invites`);

        return data.map(toInvite);
      },
    });
  },
  getAccountOwner: (customerId: CustomerId) =>
    useQuery<AccountOwner | null, MoreAppError>(() => ({
      queryKey: ["account-owner"],
      queryFn: async () => {
        try {
          const { data } = await getClient().get<AccountOwnerDto>(
            `/api/v1.0/customers/${customerId}/users/account-owner`,
          );
          return toAccountOwner(data);
        } catch (error) {
          if ((error as MoreAppError).status === 404) {
            return null;
          }
          throw error;
        }
      },
      retry: doNotRetryOnUnrecoverableError,
    })),
  transferOwnership: (customerId: CustomerId) => {
    const queryClient = useQueryClient();

    return useMutation<void, MoreAppError, TransferOwnershipRequest>(() => ({
      mutationFn: async (value) => {
        await getClient().put(`/api/v2/customers/${customerId}/users/${value.user}/grants/transfer`);
      },
      onSuccess: async (_, { user }) => {
        await queryClient.invalidateQueries({ queryKey: ["account-owner"] });
        await queryClient.invalidateQueries({ queryKey: ["currentUser"] });
        await queryClient.invalidateQueries({ queryKey: ["users"] });
        await queryClient.invalidateQueries({ queryKey: ["user", user.toString()] });
      },
    }));
  },
  updateStatus: () => {
    const queryClient = useQueryClient();

    return useMutation<void, MoreAppError, UserStatusUpdate>(() => ({
      mutationFn: async (statusUpdate) => {
        await getClient().post(`/api/v1.0/users/status`, statusUpdate);
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: ["user"] });
      },
    }));
  },
  init: init,
};

export default UserService;
